/**
 * Local persistence for an editor TEST DESIGN (+ optional test background), keyed by
 * template id. The test design is a stand-in artwork an admin (or customer) drops onto a
 * template to preview the live mockup — it composites into the print area via
 * `composeMockup`. It "saves with the template" so the same test artwork is there next
 * session.
 *
 * Testing-only: it is NOT part of the template's published assets and is never rendered
 * for customers — it just lets you eyeball the placement live. Bytes stay on the device
 * (Constitution VII); IndexedDB (not localStorage) because artwork is multi-MB binary.
 * Mirrors `mockupStore.ts`.
 */
const DB = "genz-test-designs";
const STORE = "designs";

/** A persisted test design — the artwork + an optional test background to preview behind it. */
export interface TestDesign {
  /** Encoded PNG/JPG bytes of the stand-in artwork (fills the print area). */
  design: Uint8Array;
  /** Original file name, shown in the editor as a label. */
  name: string;
  /** Optional test-background image bytes (composited behind the mockup). */
  bg?: Uint8Array | null;
  /** Optional solid test-background colour ("#rrggbb"), used when there's no bg image. */
  bgColor?: string | null;
}

const mem = new Map<string, TestDesign>();

function idb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

function idbGet(db: IDBDatabase, key: string): Promise<TestDesign | null> {
  return new Promise((resolve) => {
    const r = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
    r.onsuccess = () => resolve((r.result as TestDesign) ?? null);
    r.onerror = () => resolve(null);
  });
}

function idbPut(db: IDBDatabase, key: string, val: TestDesign): Promise<void> {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

function idbDel(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

/** Persist (or update) the test design for a template (memory + IndexedDB). */
export async function putTestDesign(templateId: string, d: TestDesign): Promise<void> {
  mem.set(templateId, d);
  const db = await idb();
  if (db) await idbPut(db, templateId, d);
}

/** Resolve the test design for a template (memory → IndexedDB), or null when none. */
export async function getTestDesign(templateId: string): Promise<TestDesign | null> {
  let d = mem.get(templateId) ?? null;
  if (!d) {
    const db = await idb();
    if (db) {
      d = await idbGet(db, templateId);
      if (d) mem.set(templateId, d);
    }
  }
  return d;
}

/** Forget the test design for a template (memory + IndexedDB). */
export async function clearTestDesign(templateId: string): Promise<void> {
  mem.delete(templateId);
  const db = await idb();
  if (db) await idbDel(db, templateId);
}

/**
 * Delete every stored test design (sign-out): this is the customer's own artwork.
 * Memory first (mirroring `purgeAllMockups`) — `getTestDesign` only consults IndexedDB on
 * a memory miss, so a stale cache would keep serving the previous account's artwork.
 */
export async function purgeAllTestDesigns(): Promise<void> {
  mem.clear();
  const db = await idb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onabort = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}
